/**
 * Web Push client — register the service worker, subscribe/unsubscribe the
 * browser to VAPID push, and mirror the subscription to the server. Push is an
 * ADDITIVE nudge channel (email is always the required one), and it is
 * deploy-gated: `getVapidPublicKey` returns null until real VAPID keys are
 * provisioned, and every entry point degrades gracefully in that case.
 */
import type { ApiClient } from '../apiClient';

export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getVapidPublicKey(api: ApiClient): Promise<string | null> {
  try {
    const res = await api.get<{ vapid_public_key: string | null }>('/api/notifications/push/public-key');
    return res.vapid_public_key;
  } catch {
    return null;
  }
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

/** Whether this device already holds an active push subscription. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (reg === undefined) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Turn ON push for this device: request permission, subscribe via VAPID, and
 * POST the subscription to the server. Returns false (no throw) if push is
 * unsupported, unprovisioned, or the user denied permission.
 */
export async function enablePush(api: ApiClient): Promise<boolean> {
  if (!isPushSupported()) return false;
  const vapid = await getVapidPublicKey(api);
  if (vapid === null) return false; // not provisioned (deploy-gated)

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = await registerServiceWorker();
  await navigator.serviceWorker.ready;
  const vapidKey = urlBase64ToUint8Array(vapid);

  // Reuse an existing subscription ONLY if it was minted with the CURRENT VAPID
  // key. After a key rotation, a stale subscription still looks "on" but every
  // push silently fails — so drop it and resubscribe with the new key.
  let existing = await reg.pushManager.getSubscription();
  if (existing !== null && !subscriptionMatchesKey(existing, vapidKey)) {
    await existing.unsubscribe().catch(() => undefined);
    existing = null;
  }
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Uint8Array<ArrayBufferLike> vs DOM BufferSource: TS 5.7 typed-array/DOM
      // lib friction, not a real hazard (same as sealApi putCiphertext).
      applicationServerKey: vapidKey as BufferSource,
    }));

  const json = sub.toJSON();
  await api.post('/api/notifications/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  });
  return true;
}

/** Turn OFF push for this device: unsubscribe locally + remove server-side. */
export async function disablePush(api: ApiClient): Promise<void> {
  const sub = await currentSubscription();
  if (sub === null) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* best-effort — still remove the server record below */
  }
  await api.del('/api/notifications/push/subscribe', { endpoint }).catch(() => undefined);
}

/** True iff the subscription's applicationServerKey equals the current VAPID key. */
function subscriptionMatchesKey(sub: PushSubscription, key: Uint8Array): boolean {
  const raw = sub.options?.applicationServerKey;
  if (raw == null) return false; // unknown key → treat as stale, resubscribe
  const existing = new Uint8Array(raw as ArrayBuffer);
  if (existing.length !== key.length) return false;
  for (let i = 0; i < key.length; i += 1) if (existing[i] !== key[i]) return false;
  return true;
}

/** VAPID keys are base64url; the PushManager wants a Uint8Array. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
